import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { UnsavedChangesFormPrompt } from '../../../../js/app/components/UnsavedChangesFormPrompt';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { AddressAutocomplete } from '../../components/address-autocomplete';
import { Button } from '../../components/button';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogCloseButton,
} from '../../components/dialog';
import { Field, Label } from '../../components/fieldset';
import { Heading } from '../../components/heading';
import { Input } from '../../components/input';
import { Textarea } from '../../components/textarea';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import { reactSelectReset } from '../../utils/utils';

const facilityValidationSchema = z.object({
  phoneNumber: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a phone number',
        fatal: false,
      });
      return;
    }
    const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (!phoneRegex.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid phone number',
        fatal: true,
      });
    }
  }),
  zipCode: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a ZIP code',
        fatal: false,
      });
      return;
    }
    if (!val.match(/^\d{5}$/)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ZIP code must be exactly 5 digits',
        fatal: true,
      });
    }
  }),
  shiftLength: z.unknown().superRefine((val, ctx) => {
    if (typeof val !== 'number' || !Number.isInteger(val) || val < 1 || val > 24) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Shift length must be a whole number between 1 and 24',
        fatal: true,
      });
    }
  }),
});

const toTimeInputValue = (timeStr?: string | null): string => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

const toTimePayload = (timeStr: string): string => {
  if (!timeStr) return timeStr;
  return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
};

interface FacilityFormValues {
  name: string;
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string | undefined;
  zipCode: string;
  type: string;
  ownership: string;
  emr: string;
  overview: string;
  phoneNumber: string;
  shiftLength: number;
  shiftStartDay: string;
  shiftStartNight: string;
  shiftStart24h: string;
  dynamicFields: Record<string, any>;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const computeInitialValues = (data: any): FacilityFormValues => {
  const dyn: Record<string, any> = {};
  if (data?.facility_type_fields) {
    data.facility_type_fields.forEach((field: any) => {
      dyn[field.field_name] = field.field_value ?? '';
    });
  }
  return {
    name: data?.name || '',
    streetAddress: data?.street_address || '',
    streetAddress2: data?.street_address2 || '',
    city: data?.city || '',
    state: data?.state,
    zipCode: data?.zip_code || '',
    type: data?.facility_type || '',
    ownership: data?.hospital_ownership || '',
    emr: data?.emr || '',
    overview: data?.extra_info || '',
    phoneNumber: data?.phone_number || '',
    shiftLength: typeof data?.shift_length === 'number' ? data.shift_length : 12,
    shiftStartDay: toTimeInputValue(data?.shift_start_day) || '07:00',
    shiftStartNight: toTimeInputValue(data?.shift_start_night) || '19:00',
    shiftStart24h: toTimeInputValue(data?.shift_start_24h) || '07:00',
    dynamicFields: dyn,
  };
};

const ValidationMessage = ({
  message,
  icon,
  colorClass,
}: {
  message?: string;
  icon?: React.ReactNode;
  colorClass?: string;
}) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

interface EditFacilityDialogProps {
  open: boolean;
  disableNameEdit?: boolean;
  onClose: () => void;
  facilityId: string;
  initialData: any;
  onSuccess?: () => void;
  facilityTypesWithDynamicFields: Record<string, any[]>;
}

const createValidationMessages = (errors: z.ZodError) => {
  const newValidationMessages: Record<
    string,
    { message: string; icon: React.ReactNode; colorClass: string }
  > = {};

  errors.errors.forEach((err) => {
    const field = err.path[0];
    newValidationMessages[field] = {
      message: err.message,
      icon: err.fatal ? (
        <XCircleIcon className="tw:h-5 tw:w-5" />
      ) : (
        <ExclamationTriangleIcon className="tw:h-5 tw:w-5" />
      ),
      colorClass: err.fatal
        ? 'tw:text-stoplight-danger tw:dark:text-red-500'
        : 'tw:text-stoplight-warning tw:dark:text-stoplight-warning',
    };
  });

  return newValidationMessages;
};

export const EditFacilityDialog: React.FC<EditFacilityDialogProps> = ({
  open,
  disableNameEdit,
  onClose,
  facilityId,
  initialData,
  onSuccess,
  facilityTypesWithDynamicFields,
}) => {
  const [name, setName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [streetAddress2, setStreetAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState<string | undefined>(undefined);
  const [zipCode, setZipCode] = useState('');
  const [type, setType] = useState('');
  const [ownership, setOwnership] = useState('');
  const [emr, setEmr] = useState('');
  const [overview, setOverview] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [shiftLength, setShiftLength] = useState<number>(12);
  const [shiftStartDay, setShiftStartDay] = useState('');
  const [shiftStartNight, setShiftStartNight] = useState('');
  const [shiftStart24h, setShiftStart24h] = useState('');
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});
  const [originalType, setOriginalType] = useState<string>('');
  const [originalFacilityTypeFields, setOriginalFacilityTypeFields] = useState<any[]>([]);
  const [showTypeChangeWarning, setShowTypeChangeWarning] = useState(false);
  const [validationMessages, setValidationMessages] = useState<
    Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
  >({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Populate the form ONCE per facility. Background refetches (5-min staleTime,
  // refetchOnWindowFocus) hand us a new initialData object with the same id — we must not
  // re-run population and wipe the user's unsaved edits.
  const populatedForIdRef = React.useRef<string | null>(null);
  const initialSnapshotRef = React.useRef<FacilityFormValues | null>(null);

  const dynamicChoicesCategories = useMemo(() => {
    const fields = initialData?.facility_type_fields || [];
    const selectFields = fields.filter(
      (field: any) => field.field_type === 'select' && field.choices_category
    );
    return selectFields.map((field: any) => field.choices_category);
  }, [initialData]);

  const allCategories = useMemo(() => {
    const baseCategories = [
      'FacilityTypes',
      'HospitalOwnershipChoices',
      'EMRChoices',
      'PostalStateChoices',
      'FacilityContactRoles',
      'Specialty',
    ];
    return [...new Set([...baseCategories, ...dynamicChoicesCategories])];
  }, [dynamicChoicesCategories]);

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories(allCategories);

  const facilityTypesOptions = useMemo(
    () => codeDefinitionsByCategory?.FacilityTypes || [],
    [codeDefinitionsByCategory]
  );

  const hospitalOwnershipOptions = useMemo(
    () => codeDefinitionsByCategory?.HospitalOwnershipChoices || [],
    [codeDefinitionsByCategory]
  );

  const emrOptions = useMemo(
    () => codeDefinitionsByCategory?.EMRChoices || [],
    [codeDefinitionsByCategory]
  );

  const stateOptions = useMemo(
    () => codeDefinitionsByCategory?.PostalStateChoices || [],
    [codeDefinitionsByCategory]
  );

  const dynamicFieldChoices = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const choicesMap: Record<string, any[]> = {};
    dynamicChoicesCategories.forEach((category: string) => {
      choicesMap[category] = codeDefinitionsByCategory[category] || [];
    });
    return choicesMap;
  }, [codeDefinitionsByCategory, dynamicChoicesCategories]);

  const codeDefinitionsMap = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const map: Record<string, string> = {};
    Object.values(codeDefinitionsByCategory)
      .flat()
      .forEach((def) => {
        map[def.code] = def.title;
      });
    return map;
  }, [codeDefinitionsByCategory]);

  // Populate form state ONCE per facility when the dialog opens. A background refetch hands
  // us a new initialData object with the same id; re-running this would wipe unsaved edits.
  useEffect(() => {
    if (!open) {
      populatedForIdRef.current = null; // next open repopulates with fresh data
      return;
    }
    if (!initialData) return;
    if (populatedForIdRef.current === facilityId) return; // already populated → preserve edits

    const values = computeInitialValues(initialData);
    setName(values.name);
    setStreetAddress(values.streetAddress);
    setStreetAddress2(values.streetAddress2);
    setCity(values.city);
    setState(values.state);
    setZipCode(values.zipCode);
    setType(values.type);
    setOwnership(values.ownership);
    setEmr(values.emr);
    setOverview(values.overview);
    setPhoneNumber(values.phoneNumber);
    setShiftLength(values.shiftLength);
    setShiftStartDay(values.shiftStartDay);
    setShiftStartNight(values.shiftStartNight);
    setShiftStart24h(values.shiftStart24h);
    setDynamicFields(values.dynamicFields);

    setShowTypeChangeWarning(false);
    setValidationMessages({});

    populatedForIdRef.current = facilityId;
    initialSnapshotRef.current = values;
  }, [open, initialData, facilityId]);

  useEffect(() => {
    if (!open) {
      setOriginalType('');
      setOriginalFacilityTypeFields([]);
    }
  }, [open]);

  useEffect(() => {
    if (hasAttemptedSubmit) {
      const result = facilityValidationSchema.safeParse({
        phoneNumber,
        zipCode,
        shiftLength,
      });

      if (!result.success) {
        setValidationMessages(createValidationMessages(result.error));
      } else {
        setValidationMessages({});
      }
    }
  }, [phoneNumber, zipCode, shiftLength, hasAttemptedSubmit]);

  useEffect(() => {
    if (!open) {
      setValidationMessages({});
      setHasAttemptedSubmit(false);
    }
  }, [open]);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: any) =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesUpdate({
        id: facilityId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Facility updated successfully!');
      queryClient.invalidateQueries();
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to update facility' });
    },
  });

  const handleTypeChange = (newType: string) => {
    if (newType !== type) {
      // Only store original type and fields if we haven't already
      if (!originalType) {
        setOriginalType(type);
        setOriginalFacilityTypeFields(initialData.facility_type_fields || []);
      }

      setShowTypeChangeWarning(true);

      setType(newType);
      initialData.facility_type = newType;

      if (facilityTypesWithDynamicFields[newType]) {
        initialData.facility_type_fields = facilityTypesWithDynamicFields[newType];
        const initialDynamicFields: Record<string, any> = {};
        initialData.facility_type_fields.forEach((field: any) => {
          initialDynamicFields[field.field_name] = '';
        });
        setDynamicFields(initialDynamicFields);
      } else {
        initialData.facility_type_fields = [];
        setDynamicFields({});
      }
    }
  };

  const handleUndoTypeChange = () => {
    setType(originalType);
    initialData.facility_type = originalType;

    initialData.facility_type_fields = originalFacilityTypeFields;

    const originalDynamicFields: Record<string, any> = {};
    originalFacilityTypeFields.forEach((field: any) => {
      originalDynamicFields[field.field_name] = field.field_value ?? '';
    });
    setDynamicFields(originalDynamicFields);
    setShowTypeChangeWarning(false);
  };

  const handleDynamicFieldChange = (fieldName: string, value: any) => {
    setDynamicFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    const result = facilityValidationSchema.safeParse({
      phoneNumber,
      zipCode,
      shiftLength,
    });

    if (!result.success) {
      const hasFatalErrors = result.error.errors.some((err) => err.fatal);
      if (hasFatalErrors) {
        setValidationMessages(createValidationMessages(result.error));
        toast.error('Please fix the validation errors before submitting.');
        return;
      }
    }

    mutation.mutate({
      name,
      street_address: streetAddress,
      street_address2: streetAddress2,
      city,
      state,
      zip_code: zipCode,
      facility_type: type,
      hospital_ownership: ownership,
      emr,
      extra_info: overview,
      phone_number: phoneNumber,
      shift_length: shiftLength,
      shift_start_day: toTimePayload(shiftStartDay),
      shift_start_night: toTimePayload(shiftStartNight),
      shift_start_24h: toTimePayload(shiftStart24h),
      facility_type_fields: initialData.facility_type_fields
        ? initialData.facility_type_fields.map((field: any) => ({
            // TODO: JOE URGENT
            // the specificity of this payload will cause serious problems and is not scalable
            // the complexity of the FTF stems from handling the type change and undoing the type change
            // we need to keep initialData separate from the "currentData", which will be swapped out
            // based on the type change
            field_name: field.field_name,
            field_title: field.field_title,
            field_type: field.field_type,
            field_order: field.field_order,
            field_required: field.field_required,
            field_unit: field.field_unit,
            choices_category: field.choices_category,
            show_on_connection: field.show_on_connection,
            show_on_communication: field.show_on_communication,
            field_value:
              field.field_type === 'number'
                ? dynamicFields[field.field_name] === ''
                  ? null
                  : Number(dynamicFields[field.field_name])
                : dynamicFields[field.field_name],
          }))
        : [],
    });
  };

  const currentValues: FacilityFormValues = {
    name,
    streetAddress,
    streetAddress2,
    city,
    state,
    zipCode,
    type,
    ownership,
    emr,
    overview,
    phoneNumber,
    shiftLength,
    shiftStartDay,
    shiftStartNight,
    shiftStart24h,
    dynamicFields,
  };
  const isDirty =
    populatedForIdRef.current !== null && !isEqual(currentValues, initialSnapshotRef.current);

  const doClose = () => {
    // If we have original values stored, restore them before closing
    if (originalType) {
      initialData.facility_type = originalType;
      initialData.facility_type_fields = originalFacilityTypeFields;
    }
    setShowDiscardConfirm(false);
    onClose();
  };

  const requestClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    doClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        size="3xl"
        className="tw:flex tw:max-h-[93vh] tw:flex-col"
      >
        <div className="tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-4">
          <DialogTitle className="">Edit Facility</DialogTitle>
          <DialogCloseButton
            onClick={requestClose}
            aria-label="Close facility editor"
            className="tw:-mr-1 tw:p-1"
          />
        </div>
        {showTypeChangeWarning && (
          <div className="tw:mt-2 tw:mb-4 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
            <div className="tw:flex">
              <div className="tw:flex-shrink-0">
                <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
              </div>
              <div className="tw:ml-3">
                <p className="tw:text-sm tw:text-stoplight-warning">
                  Changing from {codeDefinitionsMap[originalType] || originalType} removes
                  information specific to that facility type. If you did not mean to change the
                  type,{' '}
                  <button
                    type="button"
                    onClick={handleUndoTypeChange}
                    className="tw:font-medium tw:underline tw:hover:text-stoplight-warning/80"
                  >
                    click here to undo.
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <DialogBody className="tw:-mx-2 tw:!mt-1 tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-2 tw:py-1">
            <div className="tw:space-y-4">
              {/* Facility Info */}
              <div className="tw:space-y-4">
                <Field className="tw:col-span-2">
                  <Label htmlFor="facility-name">
                    Facility Name <span className="tw:text-stoplight-danger">*</span>
                  </Label>
                  <Input
                    id="facility-name"
                    value={name}
                    disabled={disableNameEdit}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field>
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-3">
                  <Field>
                    <Label htmlFor="facility-type">
                      Type <span className="tw:text-stoplight-danger">*</span>
                    </Label>
                    <Select
                      options={facilityTypesOptions.map((option) => ({
                        value: option.code,
                        label: option.title,
                      }))}
                      value={
                        facilityTypesOptions.find((t) => t.code === type)
                          ? {
                              value: type,
                              label: facilityTypesOptions.find((t) => t.code === type)?.title || '',
                            }
                          : null
                      }
                      onChange={(option) => handleTypeChange(option?.value || '')}
                      placeholder="Select a facility type"
                      className="tw:w-full"
                      styles={reactSelectReset}
                    />
                  </Field>
                  <Field>
                    <Label>Ownership</Label>
                    <Select
                      options={hospitalOwnershipOptions.map((option) => ({
                        value: option.code,
                        label: option.title,
                      }))}
                      value={
                        hospitalOwnershipOptions.find((t) => t.code === ownership)
                          ? {
                              value: ownership,
                              label:
                                hospitalOwnershipOptions.find((t) => t.code === ownership)?.title ||
                                '',
                            }
                          : null
                      }
                      onChange={(option) => setOwnership(option?.value || '')}
                      placeholder="Select an option"
                      className="tw:w-full"
                      styles={reactSelectReset}
                    />
                  </Field>
                </div>
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-3">
                  <Field>
                    <Label htmlFor="facility-emr">EMR</Label>
                    <Select
                      options={emrOptions.map((option) => ({
                        value: option.code,
                        label: option.title,
                      }))}
                      value={
                        emrOptions.find((t) => t.code === emr)
                          ? {
                              value: emr,
                              label: emrOptions.find((t) => t.code === emr)?.title || '',
                            }
                          : null
                      }
                      onChange={(option) => setEmr(option?.value || '')}
                      placeholder="Select an option"
                      className="tw:w-full"
                      styles={reactSelectReset}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="facility-phone">Phone Number</Label>
                    <Input
                      id="facility-phone"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Phone Number"
                    />
                    <ValidationMessage
                      message={validationMessages?.phoneNumber?.message}
                      icon={validationMessages?.phoneNumber?.icon}
                      colorClass={validationMessages?.phoneNumber?.colorClass}
                    />
                  </Field>
                </div>
              </div>
              {/* Address fields */}
              <div>
                <Field className="tw:md:col-span-2">
                  <Label>Address</Label>
                </Field>
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-3">
                  <Field>
                    <AddressAutocomplete
                      id="facility-street-address"
                      value={streetAddress}
                      className="tw:mt-1.5"
                      onChange={(value) => setStreetAddress(value)}
                      onPlaceSelected={(addressComponents) => {
                        setStreetAddress(addressComponents.streetAddress);
                        setStreetAddress2(addressComponents.streetAddress2 || '');
                        setCity(addressComponents.city);
                        setState(addressComponents.state);
                        setZipCode(addressComponents.zipCode);
                      }}
                      placeholder="Street Address"
                      data1pIgnore={true}
                    />
                  </Field>
                  <Field>
                    <Input
                      id="facility-street-address2"
                      value={streetAddress2}
                      className="tw:mt-1.5"
                      onChange={(e) => setStreetAddress2(e.target.value)}
                      placeholder="Street Address 2"
                      data-1p-ignore
                    />
                  </Field>
                  <Field>
                    <Input
                      id="facility-city"
                      value={city}
                      className="tw:mt-1.5"
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      data-1p-ignore
                    />
                  </Field>
                  <Field>
                    <Select
                      options={stateOptions.map((option) => ({
                        value: option.code,
                        label: option.code,
                      }))}
                      value={
                        stateOptions.find((s) => s.code === state)
                          ? {
                              value: state || '',
                              label: state || '',
                            }
                          : null
                      }
                      onChange={(option) => setState(option?.value ?? undefined)}
                      placeholder="State"
                      className="tw:mt-1.5 tw:w-full"
                      styles={reactSelectReset}
                      data-1p-ignore
                    />
                  </Field>
                  <Field>
                    <Input
                      id="facility-zip"
                      value={zipCode}
                      className="tw:mt-1.5"
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder="Zip Code"
                      data-1p-ignore
                    />
                    <ValidationMessage
                      message={validationMessages?.zipCode?.message}
                      icon={validationMessages?.zipCode?.icon}
                      colorClass={validationMessages?.zipCode?.colorClass}
                    />
                  </Field>
                </div>
              </div>

              {/* Overview */}
              <Field>
                <Label htmlFor="facility-overview">Facility Overview</Label>
                <Textarea
                  id="facility-overview"
                  value={overview}
                  onChange={(e) => setOverview(e.target.value)}
                />
              </Field>

              {/* Assignment Configuration */}
              <div>
                <div className="tw:!text-xl tw:text-zinc-900 tw:font-medium">
                  Assignment Configuration
                </div>
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-3 tw:mt-1.5">
                  <Field>
                    <Label htmlFor="facility-shift-length">Shift Length (Hours)</Label>
                    <Input
                      id="facility-shift-length"
                      type="number"
                      min={1}
                      max={24}
                      value={Number.isNaN(shiftLength) ? '' : String(shiftLength)}
                      onChange={(e) => setShiftLength(parseInt(e.target.value, 10))}
                    />
                    <ValidationMessage
                      message={validationMessages?.shiftLength?.message}
                      icon={validationMessages?.shiftLength?.icon}
                      colorClass={validationMessages?.shiftLength?.colorClass}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="facility-shift-start-24h">24 Hr Shift Start Time</Label>
                    <Input
                      id="facility-shift-start-24h"
                      type="time"
                      value={shiftStart24h}
                      onChange={(e) => setShiftStart24h(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="facility-shift-start-day">Day Shift Start Time</Label>
                    <Input
                      id="facility-shift-start-day"
                      type="time"
                      value={shiftStartDay}
                      onChange={(e) => setShiftStartDay(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="facility-shift-start-night">Night Shift Start Time</Label>
                    <Input
                      id="facility-shift-start-night"
                      type="time"
                      value={shiftStartNight}
                      onChange={(e) => setShiftStartNight(e.target.value)}
                    />
                  </Field>
                </div>
              </div>

              {/* Dynamic Fields */}
              {initialData?.facility_type_fields && initialData.facility_type_fields.length > 0 && (
                <div
                  className={`tw:rounded-lg tw:p-4 tw:bg-zinc-100 tw:dark:bg-zinc-800 ${
                    showTypeChangeWarning
                      ? 'tw:bg-stoplight-warning/10 tw:dark:!bg-stoplight-warning/10'
                      : ''
                  }`}
                >
                  {showTypeChangeWarning && (
                    <div className="tw:mb-4">
                      <p className="tw:mt-1 tw:text-sm tw:text-stoplight-warning">
                        These fields are specific to the new facility type.
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="tw:!text-xl tw:text-zinc-900 tw:font-medium">
                      {codeDefinitionsMap[type] || type} Fields
                    </div>
                  </div>
                  <DynamicFieldFactory
                    fields={initialData.facility_type_fields.map((field: any) => ({
                      ...field,
                      field_value: dynamicFields[field.field_name],
                      choices:
                        field.field_type === 'select'
                          ? dynamicFieldChoices[field.choices_category] || []
                          : undefined,
                    }))}
                    columns={2}
                    onChange={handleDynamicFieldChange}
                    readOnly={false}
                  />
                </div>
              )}
            </div>
          </DialogBody>
          <DialogActions className="tw:mt-4 tw:shrink-0 tw:border-t tw:border-zinc-200 tw:pt-4">
            <Button type="button" plain onClick={requestClose}>
              Cancel
            </Button>
            <Button type="submit" color="brandgreen0" disabled={mutation.isLoading}>
              {mutation.isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !mutation.isLoading} />
      <ConfirmationDialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={doClose}
        title="Discard unsaved changes?"
        body="You have unsaved changes that will be lost. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep editing"
        confirmVariant="danger"
      />
    </>
  );
};
